/**
 * Review Mode Selector Modal
 * Allows user to choose between Quick Review, Full Review, or Trust AI modes
 */

import React from 'react';
import { CheckCircle, Clock, Zap, AlertCircle } from 'lucide-react';

interface QualitySummary {
  overall_score?: number;
  overall_rating?: string;
  total_subdomains: number;
  critical_issues?: {
    critical: number;
  };
}

interface ReviewModeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  domainPackId: string;
  domainName: string;
  qualitySummary: QualitySummary;
  onModeSelected: (mode: 'quick' | 'full' | 'trust_ai') => void;
}

const ReviewModeSelector: React.FC<ReviewModeSelectorProps> = ({
  isOpen,
  onClose,
  domainPackId,
  domainName,
  qualitySummary,
  onModeSelected,
}) => {
  if (!isOpen) return null;

  const overallScore = qualitySummary.overall_score || 0;
  const trustAIEligible = overallScore >= 75;

  const handleModeSelect = (mode: 'quick' | 'full' | 'trust_ai') => {
    onModeSelected(mode);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">Choose Review Mode</h2>
              <p className="text-neutral-600 mt-1">{domainName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mode Cards */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Quick Review */}
          <div
            onClick={() => handleModeSelect('quick')}
            className="border-2 border-neutral-200 rounded-lg p-6 hover:border-primary-500 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                <Zap className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Quick Review</h3>
                <p className="text-sm text-neutral-500">3-5 minutes</p>
              </div>
            </div>

            <p className="text-sm text-neutral-700 mb-4">
              Focus on critical issues only. Fast validation for time-constrained reviews.
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Review {qualitySummary.critical_issues?.critical || 0} critical issues</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Simple approve/flag/reject decision</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Optional notes field</span>
              </div>
            </div>

            <div className="pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 font-medium uppercase">Best For</p>
              <p className="text-sm text-neutral-700 mt-1">
                Routine domain packs, time-constrained validation
              </p>
            </div>
          </div>

          {/* Full Review */}
          <div
            onClick={() => handleModeSelect('full')}
            className="border-2 border-neutral-200 rounded-lg p-6 hover:border-primary-500 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                <CheckCircle className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Full Review</h3>
                <p className="text-sm text-neutral-500">15-30 minutes</p>
              </div>
            </div>

            <p className="text-sm text-neutral-700 mb-4">
              Comprehensive validation with dimension-by-dimension scoring and detailed feedback.
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Review all {qualitySummary.total_subdomains} subdomains</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Score 5 dimensions per subdomain</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Provide strengths & weaknesses</span>
              </div>
            </div>

            <div className="pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 font-medium uppercase">Best For</p>
              <p className="text-sm text-neutral-700 mt-1">
                High-stakes projects, quality assurance leads
              </p>
            </div>
          </div>

          {/* Trust AI */}
          <div
            onClick={() => trustAIEligible && handleModeSelect('trust_ai')}
            className={`border-2 rounded-lg p-6 transition-all ${
              trustAIEligible
                ? 'border-neutral-200 hover:border-primary-500 hover:shadow-lg cursor-pointer group'
                : 'border-neutral-200 bg-neutral-50 cursor-not-allowed opacity-60'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                trustAIEligible
                  ? 'bg-green-100 group-hover:bg-green-600'
                  : 'bg-neutral-200'
              }`}>
                <Clock className={`w-6 h-6 transition-colors ${
                  trustAIEligible
                    ? 'text-green-600 group-hover:text-white'
                    : 'text-neutral-400'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Trust AI</h3>
                <p className="text-sm text-neutral-500">&lt;1 minute</p>
              </div>
            </div>

            {trustAIEligible ? (
              <>
                <p className="text-sm text-neutral-700 mb-4">
                  Auto-approve based on AI assessment. Instant approval with audit trail.
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">Score meets threshold ({overallScore}/100 ≥ 75)</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">Instant approval</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">Full audit trail maintained</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 font-medium uppercase">Best For</p>
                  <p className="text-sm text-neutral-700 mt-1">
                    Senior consultants, routine high-quality domain packs
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-neutral-700 mb-4">
                  Auto-approve based on AI assessment. Requires minimum score of 75/100.
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">Score below threshold ({overallScore}/100 &lt; 75)</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">Use Quick or Full Review instead</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 font-medium uppercase">Requirements</p>
                  <p className="text-sm text-neutral-700 mt-1">
                    Overall score must be 75 or higher
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 p-6 bg-neutral-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <AlertCircle className="w-4 h-4" />
              <span>All reviews create an audit trail for compliance tracking</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 hover:bg-neutral-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModeSelector;
